/******************************************************************************
 * Composant d'affichage des filtres actifs
 *****************************************************************************/

import { t } from "i18next";
import FilterDisplay from "./FilterDisplay";
import { searchWithFilters, createListQuery } from "../../services/api/search";

interface FiltersDisplayProps {
  filters?: Record<string, any>;
  onClearFilter: (field: string, newValue?: any) => void;
  onClearAll: () => void;
  size?: "sm" | "md" | "lg";
  compact?: boolean;
  entity?:
    | "workorder"
    | "replenishment"
    | "quotation"
    | "customer"
    | "service"
    | "prospect"
    | "user"
    | "part";
}

/**
 * FiltersDisplay - Affiche les filtres actifs sous forme de chips avec possibilité de les supprimer
 * @param filters - Objet contenant les filtres actifs
 * @param onClearFilter - Fonction appelée quand on supprime un filtre
 * @param onClearAll - Fonction appelée quand on supprime tous les filtres
 * @param size - Taille des chips (sm, md, lg)
 * @param compact - Mode compact pour affichage horizontal (header)
 */
const FiltersDisplay = ({
  filters,
  onClearFilter,
  onClearAll,
  size = "md",
  compact = false,
  entity
}: FiltersDisplayProps) => {
  // Déterminer s'il y a des filtres actifs
  const hasActiveFilters = filters && Object.keys(filters).some(
    (key) => filters[key] && filters[key] !== ""
  );

  if (!hasActiveFilters) {
    return null;
  }

  const baseClasses = "flex items-center gap-2";
  const compactClasses = compact 
    ? "max-w-full w-full py-1 flex-wrap" 
    : "p-2 bg-blue-gray-50 rounded-md mb-4 mt-2 flex-wrap";

  /**
   * Fonction générique pour convertir des noms d'entités (label) en IDs 
   * @param entityName - nom de l'entité (ex: "workorder_types" ou "workorder_origins")
   * @param labels - tableau de labels à convertir
   */
  const convertLabelsToIds = async (
    entityName: "workorder_types" | "workorder_origins",
    labels: string[]
  ): Promise<string[]> => {
    try {
      const response = await searchWithFilters<{ label: string; id: string }>(entityName, {
        queries: [createListQuery("label", labels, "IN")]
      }, true, false);

      const data = response.data as { label: string; id: string }[];
      const nameToIdMap = new Map(data.map(item => [item.label, item.id]));

      return labels.map(label => nameToIdMap.get(label) || label);
    } catch (error) {
      console.error(`Erreur lors de la conversion des labels en IDs pour ${entityName}:`, error);
      return labels;
    }
  };

  const convertTypeNamesToIds = (typeNames: string[]) =>
    convertLabelsToIds("workorder_types", typeNames);

  const convertOriginNamesToIds = (originNames: string[]) =>
    convertLabelsToIds("workorder_origins", originNames);

  return (
    <div className={`filters-display p-4 ${baseClasses} ${compactClasses}`}>
      {!compact && (
        <span className="text-sm font-medium  text-blue-gray-700 mr-2">
          {t("common:activeFilters")}:
        </span>
      )}

      {Object.entries(filters).map(([field, value]) => {
        if (!value || value === "") return null;

        const handleClear = async (newValue: any) => {
          if (field === "workorderType") {
            if (newValue === null) {
              onClearFilter("type", null);
            } else {
              const typeNames = Array.isArray(newValue) ? newValue : [newValue];
              const typeIds = await convertTypeNamesToIds(typeNames);
              onClearFilter("type", typeIds);
            }
          } else if (field === "workorderOrigin") {
            if (newValue === null) {
              onClearFilter("origin", null);
            } else {
              const originNames = Array.isArray(newValue) ? newValue : [newValue];
              const originIds = await convertOriginNamesToIds(originNames);
              onClearFilter("origin", originIds);
            }
          } else {
            onClearFilter(field, newValue);
          }
        };

        return (
          <FilterDisplay
            key={field}
            type={field}
            value={value}
            onClear={handleClear}
            size={compact ? "sm" : size}
            entity={entity}
          />
        );
      })}

    </div>
  );
};

export default FiltersDisplay;
